import React, { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supplierContractAPI } from '../../api/suppliers.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';

// Codes only — labels resolved at render via t('suppliers.contracts.*') below
// (module constants are evaluated once, before any language change).
const CONTRACT_TYPE_CODES = [
  'maintenance', 'leasing', 'fuel', 'tire', 'insurance_broker', 'towing',
  'glass', 'bodywork', 'parking', 'toll', 'washing', 'inspection', 'rental',
  'parts', 'other',
];

const STATUS_CODES = ['draft', 'active', 'suspended', 'expired', 'terminated', 'renewed'];

const PAYMENT_FREQ_CODES = ['monthly', 'quarterly', 'semi_annual', 'annual', 'on_demand'];

const INITIAL = {
  contract_reference: '', contract_type: 'maintenance', status: 'draft',
  start_date: '', end_date: '', amount_annual_ht: '', payment_frequency: 'monthly',
  auto_renew: true, notice_period_days: 90,
  sla_response_hours: '', sla_resolution_hours: '',
  terms_summary: '', notes: '',
};

export default function SupplierContractFormModal({ isOpen, onClose, onSaved, supplierId, contract }) {
  const { t } = useTranslation();
  const { symbolOf } = useOrgCurrency();
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  const contractTypes = CONTRACT_TYPE_CODES.map((value) => ({ value, label: t(`suppliers.contracts.types.${value}`) }));
  const statusOptions = STATUS_CODES.map((value) => ({ value, label: t(`suppliers.contracts.statuses.${value}`) }));
  const paymentFrequencies = PAYMENT_FREQ_CODES.map((value) => ({ value, label: t(`suppliers.contracts.frequencies.${value}`) }));

  const isEdit = !!contract;

  useEffect(() => {
    if (contract) {
      setForm({
        contract_reference: contract.contract_reference || '',
        contract_type: contract.contract_type || 'maintenance',
        status: contract.status || 'draft',
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        amount_annual_ht: contract.amount_annual_ht || '',
        payment_frequency: contract.payment_frequency || 'monthly',
        auto_renew: contract.auto_renew ?? true,
        notice_period_days: contract.notice_period_days || 90,
        sla_response_hours: contract.sla_response_hours || '',
        sla_resolution_hours: contract.sla_resolution_hours || '',
        terms_summary: contract.terms_summary || '',
        notes: contract.notes || '',
      });
    } else {
      setForm(INITIAL);
    }
  }, [contract]);

  if (!isOpen) return null;

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.contract_reference || !form.start_date) return;
    setSaving(true);
    try {
      const payload = { ...form, supplier_id: supplierId };
      if (payload.amount_annual_ht) payload.amount_annual_ht = parseFloat(payload.amount_annual_ht);
      if (payload.sla_response_hours) payload.sla_response_hours = parseInt(payload.sla_response_hours);
      if (payload.sla_resolution_hours) payload.sla_resolution_hours = parseInt(payload.sla_resolution_hours);
      if (payload.notice_period_days) payload.notice_period_days = parseInt(payload.notice_period_days);

      if (isEdit) {
        await supplierContractAPI.update(contract.id, payload);
      } else {
        await supplierContractAPI.create(payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('[ContractForm] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            {isEdit ? t('suppliers.contracts.form.titleEdit') : t('suppliers.contracts.form.titleNew')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.reference')}</label>
              <input type="text" value={form.contract_reference} onChange={e => set('contract_reference', e.target.value)} className={inputCls} placeholder={t('suppliers.contracts.form.referencePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.type')}</label>
              <select value={form.contract_type} onChange={e => set('contract_type', e.target.value)} className={inputCls}>
                {contractTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.status')}</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
                {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.annualAmountExclTax', { symbol: symbolOf(contract) })}</label>
              <input type="number" value={form.amount_annual_ht} onChange={e => set('amount_annual_ht', e.target.value)} className={inputCls} placeholder="12000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.startDate')}</label>
              <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.endDate')}</label>
              <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.paymentFrequency')}</label>
              <select value={form.payment_frequency} onChange={e => set('payment_frequency', e.target.value)} className={inputCls}>
                {paymentFrequencies.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.noticePeriod')}</label>
              <input type="number" value={form.notice_period_days} onChange={e => set('notice_period_days', e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* SLA */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('suppliers.contracts.form.slaTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.slaResponseHours')}</label>
                <input type="number" value={form.sla_response_hours} onChange={e => set('sla_response_hours', e.target.value)} className={inputCls} placeholder="4" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.slaResolutionHours')}</label>
                <input type="number" value={form.sla_resolution_hours} onChange={e => set('sla_resolution_hours', e.target.value)} className={inputCls} placeholder="48" />
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-3">
            <input type="checkbox" id="auto_renew" checked={form.auto_renew} onChange={e => set('auto_renew', e.target.checked)} className="rounded border-gray-300" />
            <label htmlFor="auto_renew" className="text-sm text-gray-700">{t('suppliers.contracts.form.autoRenew')}</label>
          </div>

          {/* Text fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.termsSummary')}</label>
            <textarea value={form.terms_summary} onChange={e => set('terms_summary', e.target.value)} className={inputCls} rows={2} placeholder={t('suppliers.contracts.form.termsSummaryPlaceholder')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contracts.form.notes')}</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className={inputCls} rows={2} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300">{t('common.actions.cancel')}</button>
          <button onClick={handleSave} disabled={saving || !form.contract_reference || !form.start_date} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? t('suppliers.contracts.form.saving') : isEdit ? t('common.actions.save') : t('suppliers.contracts.form.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
